import { AffordabilityFormDataType, PropertyType } from '@hex/calculators-shared/types/affordability';
import { getHemnetQueryParams, mapHemnetPropertyType } from '@hex/calculators-shared/utils';
import { trackCalculatorInit, useTrackCalculatorLoad } from '@hex/shared/tracking';
import { deepFlattenObject } from '@hex/shared/utils';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export function useAffordabilityForm() {
  const { t } = useTranslation();
  const queryString = getHemnetQueryParams();
  const defaultPropertyValue = mapHemnetPropertyType[queryString.boendetyp] || PropertyType.Villa;
  const getDefaultMonthlyPayment = () => {
    const monthlyFee = Number(queryString.manadsavgift) || 0;
    const operatingCost = Number(queryString.driftkostnad) || 0;

    return monthlyFee + operatingCost > 0 ? (monthlyFee + operatingCost).toString() : '';
  };
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, submitCount, isDirty, dirtyFields },
  } = useForm<AffordabilityFormDataType>({
    defaultValues: {
      propertyType: defaultPropertyValue,
      municipality: '',
      monthlyPayment: getDefaultMonthlyPayment(),
      downpayment: '',
      secondaryApplicantExist: null,
      primaryApplicantIncome: '',
      secondaryApplicantIncome: '',
      partnership: '',
      children: 0,
      cars: 0,
      properties: [],
      debts: [],
      loanAmount: '',
    },
  });

  const allFields = watch();

  const propertyTypeRules = { required: t('personalInformation.inputs.property.requiredError') };
  const municipalityRules = { required: 'Du måste ange kommun innan du går vidare.' };
  const monthlyPaymentRules = {
    required: t('personalInformation.inputs.estimatedExpensesPayment.requiredError'),
  };
  const downpaymentRules = { required: t('personalInformation.inputs.downPayment.requiredError') };

  const [isTracked, setIsTracked] = useState(false);
  const trackCalculatorStarted = () => {
    if (!isTracked) {
      trackCalculatorInit();
      setIsTracked(true);
    }
  };

  // focus on error functionality
  useEffect(() => {
    if (errors) {
      // errors need to be sorted according to keys in the form data type,
      // by default they are sorted in the order they were registered by RHF

      const propertyErrors: Record<string, number>[] = errors.properties
        ? (errors.properties as Record<string, number>[]).map((_property: Record<string, number>, index: number) => ({
            [`properties.${index}.loanType`]: 10.1 + 0.4 * index,
            [`properties.${index}.remainingDebt`]: 10.2 + 0.4 * index,
            [`properties.${index}.monthlyPayment`]: 10.3 + 0.4 * index,
            [`properties.${index}.creditLimit`]: 10.4 + 0.4 * index,
          }))
        : [];

      const debtErrors: Record<string, number>[] = errors.debts
        ? (errors.debts as Record<string, number>[]).map((_debt: Record<string, number>, index: number) => ({
            [`debts.${index}.loanType`]: 11.1 + 0.4 * index,
            [`debts.${index}.remainingDebt`]: 11.2 + 0.4 * index,
            [`debts.${index}.monthlyPayment`]: 11.3 + 0.4 * index,
            [`debts.${index}.creditLimit`]: 11.4 + 0.4 * index,
          }))
        : [];

      const order: Record<keyof AffordabilityFormDataType, number> = {
        propertyType: 0,
        municipality: 1,
        monthlyPayment: 2,
        downpayment: 3,
        secondaryApplicantExist: 4,
        primaryApplicantIncome: 5,
        secondaryApplicantIncome: 6,
        partnership: 7,
        children: 8,
        cars: 9,
        ...propertyErrors.reduce((acc: any, curr: any) => ({ ...acc, ...curr }), {}),
        ...debtErrors.reduce((acc: any, curr: any) => ({ ...acc, ...curr }), {}),
        loanAmount: 10,
      };

      const flattenedErrorKeys = Object.keys(deepFlattenObject(errors))
        .filter((item) => !(item.includes('.type') || item.includes('.ref.name')))
        .map((item) => item.replace('.message', ''));

      const firstError = [...flattenedErrorKeys].sort(
        (a, b) => order[a as keyof AffordabilityFormDataType] - order[b as keyof AffordabilityFormDataType],
      )[0];

      document.getElementsByName(firstError)[0]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  useTrackCalculatorLoad('borrow-calculator');

  return {
    control,
    handleSubmit,
    trackCalculatorStarted,
    reset,
    watch,
    isDirty,
    formFields: allFields,
    propertyTypeRules,
    municipalityRules,
    monthlyPaymentRules,
    downpaymentRules,
    dirtyFields,
  };
}
